import type { PollRead } from '@models/api/poll';
import { pollSomePickedMoreThanOne } from './pollFormat';

/**
 * How each option row draws (polls-B1 "Option row and bar, every state"):
 * - `live`: options only, no bars or shares (B1; the author before any answer, A7/E1);
 * - `result-open`: bars, shares, own picks ticked with the teal-ink border (B2, B5, B9, E12, B11);
 * - `result-closed`: the same, every row on the plain border, controls dimmed, leaders marked (B3);
 * - `closed-empty`: ended with no answers: no controls, bars or shares, options greyed (B4).
 */
export type PollRowState = 'live' | 'result-open' | 'result-closed' | 'closed-empty';

/** The footer's right side. */
export type PollFooterRight =
  | { kind: 'vote-to-see' }
  | { kind: 'no-answers-yet' }
  | { kind: 'no-one-answered' }
  | { kind: 'held' }
  | { kind: 'answered'; count: number; seeWho: boolean }
  /** Nothing on the right: a guest's held pick (B7b, E5), an answer that cannot join (E17, P13), or a read-only poll's ask. */
  | { kind: 'none' };

export interface PollDisplay {
  rows: PollRowState;
  right: PollFooterRight;
  /** P7: under the footer whenever results show on a multi-answer poll with extra picks. */
  somePickedMoreThanOne: boolean;
}

/**
 * The card's read-only state from the poll block (TECH-197 § Voting, § Time and ending; CONTRACT
 * § 5.2). Who sees results is the server's `results_visible` (the author, anyone with an answer,
 * held included per RESOLUTIONS F1, or a closed poll); this only decides how they draw.
 */
export const pollDisplay = (poll: PollRead, isAuthor: boolean): PollDisplay => {
  const display = pollDisplayOf(poll, isAuthor);
  // Polls off: only what is true stays (the count, "no one answered"); nothing that asks for an answer or promises one.
  const { kind } = display.right;
  return poll.read_only === true && kind !== 'answered' && kind !== 'no-one-answered'
    ? { ...display, right: { kind: 'none' } }
    : display;
};

const pollDisplayOf = (poll: PollRead, isAuthor: boolean): PollDisplay => {
  const voters = poll.voters_count ?? 0;
  // A held answer counts for nothing once the poll is closed: it is discarded, not moved in, when
  // the join is approved (TECH-197 § Voting "Approved"), so an ended poll draws as for anyone.
  const held = poll.my_answer_held && !poll.is_closed;

  if (poll.is_closed && voters === 0) {
    return {
      rows: 'closed-empty',
      right: { kind: 'no-one-answered' },
      somePickedMoreThanOne: false,
    };
  }
  if (poll.results_visible && (voters > 0 || held)) {
    return {
      rows: poll.is_closed ? 'result-closed' : 'result-open',
      right: held
        ? { kind: 'held' }
        : { kind: 'answered', count: voters, seeWho: poll.can_see_voters },
      somePickedMoreThanOne: pollSomePickedMoreThanOne(poll),
    };
  }
  return {
    rows: 'live',
    right: isAuthor ? { kind: 'no-answers-yet' } : { kind: 'vote-to-see' },
    somePickedMoreThanOne: false,
  };
};

/**
 * The options drawn and read as the viewer's own: their picks, held ones too while the poll is
 * open; a held pick on a closed poll is discarded (see above), so it is not theirs any more.
 */
export const pollOwnPickIds = (
  poll: Pick<PollRead, 'my_option_ids' | 'my_answer_held' | 'is_closed'>,
): Set<string> => new Set(poll.my_answer_held && poll.is_closed ? [] : poll.my_option_ids);
